/**
 * MobileMenuSheet - Menu mobile em forma de sheet
 * 
 * Melhorias:
 * ✅ Hook useNotification centralizado
 * ✅ Código comentado removido
 * ✅ SubMenuItem component reutilizado
 * ✅ Sem lógica ternária aninhada
 */

'use client';

import * as React from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/atoms/sheet';
import { ScrollArea } from '@/components/atoms/scroll-area';
import Link from 'next/link';
import { IMenuGroup } from '@/services/header/type';
import { useNotification } from './useNotification';
import { HamburgerIcon } from './HamburgerIcon';
import { PlusIcon } from './PlusIcon';
import { MinusIcon } from './MinusIcon';

interface MobileMenuSheetProps {
  menuGroups?: IMenuGroup[];
}

const NOTIFICATION_MESSAGE = '⚠️ Serviço brevemente disponível.';
const NOTIFICATION_DURATION = 3000;

export function MobileMenuSheet({
  menuGroups,
}: MobileMenuSheetProps) {
  const [open, setOpen] = React.useState(false);
  const [expandedMenus, setExpandedMenus] = React.useState<number[]>([]);
  const { isVisible, } = useNotification(
    NOTIFICATION_MESSAGE,
    NOTIFICATION_DURATION
  );

  const handleLinkClick = () => {
    setOpen(false);
  };

  const toggleSubmenu = (idx: number) => {
    setExpandedMenus(prev =>
      prev.includes(idx)
        ? prev.filter(i => i !== idx)
        : [...prev, idx]
    );
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger
        className="inline-flex items-center justify-center p-3 xl:hidden z-50 rounded-md hover:bg-gray-100/10 transition-colors"
        aria-label="Abrir menu"
      >
        <HamburgerIcon className="w-6 h-6" />
      </SheetTrigger>

      {/* Notificação de serviço indisponível */}
      {isVisible && (
        <div className="fixed top-4 right-4 bg-yellow-100 text-orange-600 px-4 py-2 rounded shadow z-50 transition-all duration-300">
          {NOTIFICATION_MESSAGE}
        </div>
      )}

      <SheetContent side="top" className="w-full p-0 z-50 top-16 h-auto max-h-[calc(100vh-4rem)] overflow-hidden">
        <SheetHeader className="px-6 pt-6 pb-4">
          <SheetTitle className="text-lg font-bold sr-only">Menu</SheetTitle>
        </SheetHeader>

        <ScrollArea maxH="calc(100vh - 12rem)" className="px-6 pb-6 z-50">
          <div className="space-y-0">
            {menuGroups?.map((group, idx) => {
              const hasSubMenu = group.subMenu && group.subMenu.length > 0 &&
                group.subMenu.some(subItem => subItem.subMenu && subItem.subMenu.length > 0);
              const isExpanded = expandedMenus.includes(idx);

              return (
                <div key={`menu-group-${idx}`} className="py-2 md:py-2.5 border-b border-gray-100 last:border-0">
                  <div className="flex items-center justify-between">
                    {/* Label principal */}
                    {hasSubMenu ? (
                      <button
                        onClick={() => toggleSubmenu(idx)}
                        className="block py-1 text-xs md:text-sm font-semibold text-gray-900 uppercase tracking-wide hover:text-orangefpef transition-colors flex-1 text-left"
                      >
                        {group.label}
                      </button>
                    ) : group.url ? (
                      <Link
                        href={group.url}
                        className="block py-1 text-xs md:text-sm font-semibold text-gray-900 uppercase tracking-wide hover:text-orangefpef transition-colors flex-1"
                        onClick={handleLinkClick}
                      >
                        {group.label}
                      </Link>
                    ) : (
                      <p className="py-1 text-xs md:text-sm font-semibold text-gray-900 uppercase tracking-wide flex-1">
                        {group.label}
                      </p>
                    )}

                    {/* Botão de expandir/colapsar */}
                    {hasSubMenu && (
                      <button
                        onClick={() => toggleSubmenu(idx)}
                        className="p-1.5 md:p-2 hover:bg-gray-100 rounded transition-colors shrink-0"
                        aria-label={isExpanded ? 'Fechar submenu' : 'Abrir submenu'}
                      >
                        {isExpanded ? (
                          <MinusIcon className="w-2.5 h-2.5 md:w-3 md:h-3" />
                        ) : (
                          <PlusIcon className="w-2.5 h-2.5 md:w-3 md:h-3" />
                        )}
                      </button>
                    )}
                  </div>

                  {/* Submenus - apenas quando expandido */}
                  {hasSubMenu && isExpanded && (
                    <div className="mt-1.5 md:mt-2 space-y-0">
                      {group.subMenu?.map((subItem, subIdx) => {
                        if (!subItem.subMenu || subItem.subMenu.length === 0) return null;

                        return subItem.subMenu.map((button, btnIdx) => (
                          <div key={`submenu-${idx}-${subIdx}-${btnIdx}`}>
                            {button.external_link ? (
                              <a
                                href={button.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block py-1.5 md:py-2 pl-3 md:pl-4 text-[10px] md:text-xs text-gray-900 hover:text-orangefpef transition-colors uppercase"
                                onClick={handleLinkClick}
                              >
                                {button.label}
                              </a>
                            ) : (
                              <Link
                                href={button.url}
                                className="block py-1.5 md:py-2 pl-3 md:pl-4 text-[10px] md:text-xs text-gray-900 hover:text-orangefpef transition-colors uppercase"
                                onClick={handleLinkClick}
                              >
                                {button.label}
                              </Link>
                            )}
                          </div>
                        ));
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Portal Kre+ Button - COMENTADO */}
            {/* <div className="py-6 border-t border-gray-200">
              <Link
                href="#"
                className="flex items-center justify-center px-4 py-2 rounded-full text-sm font-semibold transition-all w-full cursor-pointer"
                style={{
                  backgroundColor: HEADER_COLORS.primary.teal,
                  color: 'white',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.9';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
                onClick={handleLinkClick}
              >
                Portal Kre+
              </Link>
            </div> */}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}